import MetricCardSkeleton from "./MetricCardSkeleton";
import { getMetricGridColumnConfig, type MetricGridLayout } from "@/components/MetricGrid/metricGridLayout";
import { useMetricGridBalancedColumns } from "@/components/MetricGrid/useMetricGridBalancedColumns";

/**
 * Props for MetricGridSkeleton component
 * @param cards - Number of metric cards to display (default: 3)
 * @param layout - Fixed column breakpoints; ignored when `minCardWidth` is set. Pair with the same value on `MetricGrid`.
 * @param minCardWidth - Minimum card width in px; enables balanced wrapping. Pair with the same value on `MetricGrid`.
 * @param hasHeading - When true, omits default `mt-12`. Set when a heading skeleton already sits above the grid.
 * @param className - Additional CSS classes
 */
type MetricGridSkeletonProps = {
  cards?: number;
  layout?: MetricGridLayout;
  minCardWidth?: number;
  /** When true, omits default `mt-12`. Set only when a heading already sits above the grid — not when the grid is first under a tab bar. */
  hasHeading?: boolean;
  className?: string;
};

const DEFAULT_GRID_CLASSES = "grid-cols-1 md:grid-cols-3 lg:grid-cols-4";

export default function MetricGridSkeleton({
  cards = 3,
  layout,
  minCardWidth,
  hasHeading = false,
  className = ""
}: MetricGridSkeletonProps) {
  const { gridRef, balancedColumnCount, isMdUp } = useMetricGridBalancedColumns(cards, minCardWidth);

  const { className: columnClassName, style } =
    layout != null || minCardWidth != null
      ? getMetricGridColumnConfig({
          layout: layout ?? "balanced",
          minCardWidth,
          balancedColumnCount,
          isMdUp
        })
      : { className: DEFAULT_GRID_CLASSES, style: undefined };

  return (
    <div className={`${!hasHeading ? "mt-12" : ""} space-y-8 ${className}`} data-testid="metric-grid-skeleton">
      <div ref={gridRef} className={`grid gap-3 items-stretch auto-rows-fr ${columnClassName}`} style={style}>
        {Array.from({ length: cards }).map((_, index) => (
          <MetricCardSkeleton key={index} />
        ))}
      </div>
    </div>
  );
}
